import { GroupCondition } from '@/api/enums';
import { equal, group as groupFilter } from '@/api/helpers';
import { FilterCondition, GroupedFilterConditions } from '@/api/types';
import { Account } from '@/client/accounts';
import { Branch } from '@/client/branches';
import {
  Course,
  CourseEnrollmentTypeEnum,
  CourseEntityScheduleEnum,
  CourseScheduleType,
  EnrollFormValues,
  UserStatusEnum,
} from '@/client/courses';
import { Group } from '@/client/groups';
import { User } from '@/client/users';
import { courseScheduleFactory } from '@/components/courses/modals/courseSchedule.factory';
import {
  BranchesMultiselectInput,
  GroupsMultiselectInput,
  UsersMultiselectInput,
} from '@/components/form/selectors';
import { AppButton } from '@/ui/buttons';
import { AppCalendar } from '@/ui/calendar';
import { FlexContainer } from '@/ui/styled-ui';
import { getEnrollMinDate, getUpcomingReleaseDate } from '@/utils/helpers';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { Dialog, DialogProps } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { FormEvent } from 'primereact/ts-helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AnyObject, ObjectSchema } from 'yup';

const StyledDialog = styled(Dialog)`
  width: 500px;
`;

type EnrollModalProps = {
  onSubmit: (data: EnrollFormValues) => void;
  account: Account;
  type?: CourseEnrollmentTypeEnum;
  branch?: Branch;
  group?: Group;
  user?: User;
  enrollType: CourseEntityScheduleEnum | undefined;
  maxDate?: Date;
  course?: Course;
  courseId?: string;
  isDemoAccount?: boolean;
} & DialogProps;

export const EnrollModal: React.FC<EnrollModalProps> = ({
  enrollType,
  onSubmit,
  onHide,
  account,
  branch,
  group,
  user,
  visible,
  maxDate,
  course,
  type,
  courseId,
  isDemoAccount,
}) => {
  const { t } = useTranslation();
  const [initialValues, setInitialValues] = useState<EnrollFormValues>({
    courses: [],
    branches: branch ? [branch] : [],
    groups: group ? [group] : [],
    users: user ? [user] : [],
    accounts: [account],
    date: null,
    autoEnroll: false,
    type: CourseScheduleType.STATIC,
  });
  const [warningMessage, setWarningMessage] = useState<string>();
  const [modalHeading, setModalHeading] = useState<string>();
  const [validationSchema, setValidationSchema] =
    useState<ObjectSchema<AnyObject>>();

  useEffect(() => {
    if (!type) return;
    const callbacks = courseScheduleFactory(
      t,
      type,
      account,
      branch,
      group,
      user,
    );

    if (enrollType && callbacks[enrollType]) {
      callbacks[enrollType](
        setWarningMessage,
        setModalHeading,
        setValidationSchema,
      );
    }

    setInitialValues({
      courses: [],
      branches: branch ? [branch] : [],
      groups: group ? [group] : [],
      users: user ? [user] : [],
      accounts: [account],
      date: getUpcomingReleaseDate(course?.releaseDate),
      autoEnroll: false,
      type: CourseScheduleType.STATIC,
    });
  }, [enrollType, account, branch, group, user, type]);
  const isEnroll = type === CourseEnrollmentTypeEnum.ENROLL;
  const filters: (GroupedFilterConditions | FilterCondition)[] = [];

  if (isEnroll) {
    filters.push(equal('account', account?.id), equal('active', true));
  }

  if (!isEnroll && enrollType === CourseEntityScheduleEnum.USER) {
    filters.push(
      groupFilter(GroupCondition.OR, [
        equal('status', UserStatusEnum.ENROLLED),
        equal('status', UserStatusEnum.COMPLETED),
        equal('status', UserStatusEnum.FORCE_COMPLETED),
        equal('status', UserStatusEnum.IN_PROGRESS),
      ]),
    );
  }

  return (
    <StyledDialog
      blockScroll
      visible={visible}
      header={<h1>{modalHeading}</h1>}
      onHide={onHide}
      draggable={false}
      data-testid={`${type}-${enrollType}-modal`}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={onSubmit}
      >
        {({ setFieldValue, values, errors }) => {
          return (
            <Form>
              {enrollType === CourseEntityScheduleEnum.BRANCH && !branch && (
                <div className="field w-full">
                  <label htmlFor="branches">
                    {`${t('branches')} (${values?.branches?.length ?? 0})`}
                  </label>
                  <BranchesMultiselectInput
                    className="w-full"
                    onChange={(branches) => setFieldValue('branches', branches)}
                    additionalFilters={filters}
                    selectedOptions={values.branches}
                    accountId={account?.id}
                  />
                </div>
              )}

              {enrollType === CourseEntityScheduleEnum.GROUP && !group && (
                <div className="field w-full">
                  <label htmlFor="groups">
                    {`${t('groups')} (${values?.groups?.length ?? 0})`}
                  </label>
                  <GroupsMultiselectInput
                    className="w-full"
                    onChange={(groups) => setFieldValue('groups', groups)}
                    additionalFilters={filters}
                    selectedOptions={values.groups}
                    accountId={account?.id}
                  />
                </div>
              )}

              {enrollType === CourseEntityScheduleEnum.USER && !user && (
                <div className="field w-full">
                  <label htmlFor="users">
                    {`${t('users')} (${values?.users?.length ?? 0})`}
                  </label>
                  <UsersMultiselectInput
                    courseId={courseId}
                    className="w-full"
                    onChange={(users) => setFieldValue('users', users)}
                    additionalFilters={filters}
                    selectedOptions={values.users}
                    accountId={account?.id}
                  />
                </div>
              )}

              {isEnroll && (
                <div className="field w-full mb-4">
                  <label>{t('generic.enrollDate')}</label>
                  <AppCalendar
                    id="enroll-date"
                    name="enrollDate"
                    className="w-full"
                    placeholder={t('courses.select.enrollDate')}
                    minDate={getEnrollMinDate(course?.releaseDate)}
                    maxDate={maxDate ? new Date(maxDate as Date) : undefined}
                    value={values.date}
                    onChange={(event: FormEvent) => {
                      setFieldValue(
                        'date',
                        moment(event.value?.toString()).utc(true).toDate(),
                      );
                    }}
                    readOnlyInput
                  />
                </div>
              )}

              {!isEnroll && enrollType === CourseEntityScheduleEnum.ACCOUNT && (
                <div>
                  {t('courses.unenroll.accountName.message', {
                    name: account.name,
                  })}
                </div>
              )}

              {isEnroll && <Message severity="warn" text={warningMessage} />}

              <FlexContainer justify="flex-end" className="mt-5">
                <AppButton
                  label={t('button.cancel')}
                  severity="secondary"
                  type="outlined"
                  onClick={onHide}
                  className="mr-3"
                  data-testid={`enroll-${enrollType}-cancel-form`}
                />
                <AppButton
                  label={isEnroll ? t('generic.enroll') : t('generic.unenroll')}
                  isSubmit={!isDemoAccount}
                  isDisabled={!!Object.keys(errors).length}
                  data-testid={`${type}-${enrollType}-submit-form`}
                />
              </FlexContainer>
            </Form>
          );
        }}
      </Formik>
    </StyledDialog>
  );
};
